import { useEffect, useState } from 'react';
import { ChevronLeft, CheckCircle2, XCircle, Loader2, Clock, RefreshCw } from 'lucide-react';
import supabase from '../../lib/supabase';

interface AdminVerificationsScreenProps {
  onNavigate: (screen: any) => void;
}

type Verification = {
  id: string;
  user_id: string;
  status: string;
  created_at: string;
  ai_confidence: number | null;
  ai_reasoning: string | null;
  extracted_name: string | null;
  extracted_eid: string | null;
  extracted_dob: string | null;
  extracted_expiry: string | null;
  id_front_path: string | null;
  id_back_path: string | null;
  selfie_path: string | null;
};

export default function AdminVerificationsScreen({ onNavigate }: AdminVerificationsScreenProps) {
  const [items, setItems] = useState<Verification[]>([]);
  const [signedUrls, setSignedUrls] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);
  const [actingId, setActingId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = async () => {
    setLoading(true);
    setError(null);
    const { data, error } = await supabase
      .from('identity_verifications')
      .select(`
        id, user_id, status, created_at,
        ai_confidence, ai_reasoning,
        extracted_name, extracted_eid, extracted_dob, extracted_expiry,
        id_front_path, id_back_path, selfie_path
      `)
      .eq('status', 'pending_review')
      .order('created_at', { ascending: false });

    if (error) {
      setError(error.message);
      setLoading(false);
      return;
    }
    setItems(data ?? []);

    // Best-effort signed URLs for preview thumbnails. Admins can view
    // via RLS bypass once is_admin = true; service-role signing happens
    // in the edge function for the actual OpenAI call.
    const pathsToSign = (data ?? []).flatMap((v) =>
      [v.id_front_path, v.selfie_path].filter((p): p is string => !!p)
    );
    const signed: Record<string, string> = {};
    for (const path of pathsToSign) {
      const { data: s } = await supabase.storage
        .from('identity-documents')
        .createSignedUrl(path, 600);
      if (s?.signedUrl) signed[path] = s.signedUrl;
    }
    setSignedUrls(signed);
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const act = async (id: string, action: 'approve' | 'reject') => {
    setActingId(id);
    setError(null);
    const { error } = await supabase.functions.invoke('review-identity-verification', {
      body: { verificationId: id, action },
    });
    setActingId(null);
    if (error) {
      setError(error.message);
      return;
    }
    setItems((prev) => prev.filter((v) => v.id !== id));
  };

  return (
    <div className="w-full min-h-screen bg-background pb-8">
      <div className="sticky top-0 z-10 bg-background/95 backdrop-blur-sm border-b border-border px-4 py-3">
        <div className="flex items-center gap-3">
          <button
            onClick={() => onNavigate('profile')}
            className="w-10 h-10 rounded-full hover:bg-secondary flex items-center justify-center"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
          <div className="flex-1">
            <h1 className="text-lg">Pending Verifications</h1>
            <p className="text-xs text-muted-foreground">
              {items.length} waiting for review
            </p>
          </div>
          <button
            onClick={load}
            className="w-10 h-10 rounded-full hover:bg-secondary flex items-center justify-center"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      <div className="px-4 py-4 space-y-3">
        {error && (
          <div className="bg-destructive/10 border border-destructive/20 rounded-xl p-3 text-sm text-destructive">
            {error}
          </div>
        )}

        {loading && items.length === 0 && (
          <div className="text-center text-muted-foreground py-12 text-sm">Loading…</div>
        )}

        {!loading && items.length === 0 && !error && (
          <div className="text-center text-muted-foreground py-12">
            <Clock className="w-10 h-10 mx-auto mb-3 opacity-50" />
            <div className="text-sm">No verifications waiting for review.</div>
          </div>
        )}

        {items.map((v) => (
          <div key={v.id} className="bg-card border border-border rounded-2xl p-4 space-y-3">
            <div className="flex gap-3">
              {v.id_front_path && signedUrls[v.id_front_path] && (
                <img
                  src={signedUrls[v.id_front_path]}
                  alt="ID front"
                  className="w-24 h-16 object-cover rounded-lg border border-border"
                />
              )}
              {v.selfie_path && signedUrls[v.selfie_path] && (
                <img
                  src={signedUrls[v.selfie_path]}
                  alt="Selfie"
                  className="w-16 h-16 object-cover rounded-lg border border-border"
                />
              )}
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium truncate">
                  {v.extracted_name || '(name not extracted)'}
                </div>
                <div className="text-xs text-muted-foreground">
                  EID: {v.extracted_eid || '—'}
                </div>
                <div className="text-xs text-muted-foreground">
                  Confidence: {v.ai_confidence == null ? '—' : (v.ai_confidence * 100).toFixed(0) + '%'}
                </div>
              </div>
            </div>

            {v.ai_reasoning && (
              <div className="text-xs text-muted-foreground bg-secondary rounded-lg p-2 whitespace-pre-wrap">
                {v.ai_reasoning}
              </div>
            )}

            <div className="flex gap-2">
              <button
                onClick={() => act(v.id, 'reject')}
                disabled={actingId === v.id}
                className="flex-1 h-11 rounded-xl bg-destructive/10 hover:bg-destructive/20 text-destructive border border-destructive/30 text-sm flex items-center justify-center gap-2 disabled:opacity-50"
              >
                {actingId === v.id ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <>
                    <XCircle className="w-4 h-4" />
                    Reject
                  </>
                )}
              </button>
              <button
                onClick={() => act(v.id, 'approve')}
                disabled={actingId === v.id}
                className="flex-1 h-11 rounded-xl bg-[#1A6B4F] hover:bg-[#14563F] text-white text-sm flex items-center justify-center gap-2 disabled:opacity-50"
              >
                {actingId === v.id ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <>
                    <CheckCircle2 className="w-4 h-4" />
                    Approve
                  </>
                )}
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
